<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import BackButton from '../../BackButton.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import PaymentsService, { type IncomingPaymentDetails } from '../../../lib/api/admin/payments.service';
  import toast from 'svelte-french-toast';

  export let paymentUuid: string = '';

  let isLoading = true;
  let error = '';
  let paymentData: IncomingPaymentDetails | null = null;
  let expandedCharities: Record<string, boolean> = {};

  // Track previous UUID to reload when it changes
  let previousUuid = '';
  $: if (paymentUuid && paymentUuid !== previousUuid) {
    previousUuid = paymentUuid;
    loadPaymentDetails();
  }

  // Breadcrumbs items
  $: breadcrumbsItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Incoming Payments', href: '/admin/payments/incoming' },
    { label: paymentData ? `Payment - ${paymentData.employer.name}` : 'Payment Detail' }
  ];

  function toggleCharity(charityUuid: string) {
    const currentState = expandedCharities[charityUuid] || false;
    expandedCharities = {
      ...expandedCharities,
      [charityUuid]: !currentState
    };
  }

  function isCharityExpanded(charityUuid: string): boolean {
    return expandedCharities[charityUuid] === true;
  }

  async function loadPaymentDetails() {
    if (!paymentUuid) {
      error = 'Payment UUID is required';
      isLoading = false;
      return;
    }

    try {
      isLoading = true;
      error = '';

      const response = await PaymentsService.getIncomingPaymentDetails(paymentUuid);

      if (response.success && response.data) {
        paymentData = response.data;
      } else {
        error = response.message || 'Failed to load payment details';
        toast.error(error);
      }
    } catch (err: any) {
      error = err.message || 'An error occurred while loading payment details';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  function formatCurrency(amount: number | string): string {
    return `$${parseFloat(String(amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  function handleBack() {
    push('/admin/payments/incoming');
  }

  onMount(() => {
    if (paymentUuid) {
      previousUuid = paymentUuid;
      loadPaymentDetails();
    } else {
      error = 'Payment UUID is required';
      isLoading = false;
    }
  });
</script>

<div class="incoming-payment-detail-page">
  <Breadcrumbs items={breadcrumbsItems} />
  <BackButton text="Back to Incoming Payments" onClick={handleBack} />

  {#if isLoading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState
      message="Failed to load payment details"
      description={error}
      onRetry={loadPaymentDetails}
    />
  {:else if paymentData}
    <!-- Payment Header Section -->
    <div class="payment-header">
      <div class="header-info">
        <h1 class="payment-title">
          {#if paymentData.invoice_number}
            Invoice #{paymentData.invoice_number}
          {:else}
            Payment Detail
          {/if}
        </h1>
        <div class="payment-meta">
          <span class="meta-badge" class:pending={paymentData.type === 'pending'} class:paid={paymentData.type === 'paid'}>
            {paymentData.type === 'paid' ? 'Paid by Employer' : 'Awaiting Employer Payment'}
          </span>
          {#if paymentData.payment_date}
            <span class="meta-separator">•</span>
            <span class="meta-item">Paid on {formatDate(paymentData.payment_date)}</span>
          {/if}
          {#if paymentData.reference_number}
            <span class="meta-separator">•</span>
            <span class="meta-item">Ref: {paymentData.reference_number}</span>
          {/if}
        </div>
        <div class="payment-summary">
          <span>{paymentData.totalDonations} donation{paymentData.totalDonations !== 1 ? 's' : ''}</span>
          <span class="separator">•</span>
          <span>{paymentData.totalHours.toFixed(1)} hours</span>
          <span class="separator">•</span>
          <span>{paymentData.totalCharities} charities</span>
          <span class="separator">•</span>
          <span class="total-amount">Total: {formatCurrency(paymentData.totalAmount)}</span>
        </div>
        <div class="contribution-summary-top">
          <span class="contribution-label">Total Contribution (Employer):</span>
          <span class="contribution-value">{formatCurrency(paymentData.totalContribution)}</span>
          <span class="contribution-note">(included in total above)</span>
        </div>
        <!-- Next-step hint: tells the admin whose action is pending -->
        <div class="next-step-hint" class:paid={paymentData.type === 'paid'} class:pending={paymentData.type === 'pending'}>
          {#if paymentData.type === 'paid'}
            The employer has paid these funds to Leave2Give. Next step: pay it out to the charity from <a href="/admin/pending-payouts" on:click|preventDefault={() => push('/admin/pending-payouts')}>Pending Payouts</a>.
          {:else}
            Waiting on the employer to pay this invoice. No action needed from Leave2Give yet.
          {/if}
        </div>
      </div>
    </div>

    <!-- Employer Details Card -->
    <div class="employer-details-card">
      <div class="card-header">
        <h2>Employer Information</h2>
      </div>
      <div class="card-content">
        <div class="details-grid">
          <div class="detail-item">
            <span class="detail-label">Employer Name</span>
            <span class="detail-value">{paymentData.employer.name}</span>
          </div>
          {#if paymentData.employer.abn}
            <div class="detail-item">
              <span class="detail-label">ABN</span>
              <span class="detail-value">{paymentData.employer.abn}</span>
            </div>
          {/if}
          {#if paymentData.employer.contact_name}
            <div class="detail-item">
              <span class="detail-label">Contact Name</span>
              <span class="detail-value">{paymentData.employer.contact_name}</span>
            </div>
          {/if}
          {#if paymentData.employer.contact_email}
            <div class="detail-item">
              <span class="detail-label">Contact Email</span>
              <span class="detail-value">
                <a href="mailto:{paymentData.employer.contact_email}" class="email-link">
                  {paymentData.employer.contact_email}
                </a>
              </span>
            </div>
          {/if}
          {#if paymentData.employer.contact_phone}
            <div class="detail-item">
              <span class="detail-label">Contact Phone</span>
              <span class="detail-value">{paymentData.employer.contact_phone}</span>
            </div>
          {/if}
          {#if paymentData.employer.head_office_location}
            <div class="detail-item">
              <span class="detail-label">Head Office Location</span>
              <span class="detail-value">{paymentData.employer.head_office_location}</span>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Donators grouped by charity -->
    <div class="donators-section">
      <h2 class="section-title">Donations by Charity</h2>
      {#each paymentData.charities as charityGroup (charityGroup.charity.uuid)}
        <div class="charity-group">
          <button 
            class="charity-header-button"
            on:click|stopPropagation={() => {
              toggleCharity(charityGroup.charity.uuid);
            }}
            type="button"
            aria-expanded={expandedCharities[charityGroup.charity.uuid] || false}
          >
            <div class="charity-header">
              <div class="charity-header-content">
                <h3 class="charity-name">{charityGroup.charity.name}</h3>
                <div class="charity-summary">
                  <span>{charityGroup.donators.length} donation{charityGroup.donators.length !== 1 ? 's' : ''}</span>
                  <span class="separator">•</span>
                  <span>{charityGroup.totalHours.toFixed(1)} hours</span>
                  <span class="separator">•</span>
                  <span class="charity-total">{formatCurrency(charityGroup.totalAmount)}</span>
                  <span class="separator">•</span>
                  <span class="charity-contribution">Contribution: {formatCurrency(charityGroup.totalContribution)} <span class="included-note">(included)</span></span>
                </div>
              </div>
              <svg 
                class="expand-icon" 
                class:expanded={expandedCharities[charityGroup.charity.uuid]}
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </button>
          {#if expandedCharities[charityGroup.charity.uuid]}
            <div class="donators-list">
              {#each charityGroup.donators as donator (donator.uuid)}
                <div class="donator-item">
                  <div class="donator-info">
                    <span class="donator-name">{donator.name}</span>
                    <span class="donator-hours">{donator.hours} hours</span>
                  </div>
                  <div class="donator-amounts">
                    <span class="donator-total">{formatCurrency(donator.amount)}</span>
                    <span class="donator-contribution">Contribution: {formatCurrency(donator.employer_top_up || donator.contribution || 0)} <span class="included-note">(included)</span></span>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .incoming-payment-detail-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem 2rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .payment-header {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .header-info {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .payment-title {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .payment-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .meta-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  /* Canonical palette: Paid by Employer = cyan (employer-paid); Awaiting = amber. */
  .meta-badge.paid {
    background-color: #cffafe;
    color: #155e75;
  }

  .meta-badge.pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  .next-step-hint {
    margin-top: 0.875rem;
    padding: 0.625rem 0.875rem;
    border-radius: 8px;
    font-size: 0.875rem;
    line-height: 1.4;
  }

  .next-step-hint.paid {
    background-color: #cffafe;
    color: #155e75;
  }

  .next-step-hint.pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  .next-step-hint a {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
  }

  .meta-separator {
    color: #d1d5db;
  }

  .payment-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .separator {
    color: #d1d5db;
  }

  .total-amount {
    font-weight: 600;
    color: #111827;
    font-size: 1rem;
  }

  .contribution-summary-top {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
  }

  .contribution-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .contribution-value {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
  }

  .contribution-note {
    font-size: 0.75rem;
    color: #6b7280;
    font-style: italic;
  }

  .employer-details-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .card-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .card-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .card-content {
    padding: 1.5rem 2rem;
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .detail-value {
    font-size: 1rem;
    color: #111827;
  }

  .email-link {
    color: #3b82f6;
    text-decoration: none;
  }

  .email-link:hover {
    text-decoration: underline;
  }

  .donators-section {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .charity-group {
    background: #f9fafb;
    border-radius: 8px;
    padding: 0;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    margin-bottom: 1rem;
  }

  .charity-group:last-child {
    margin-bottom: 0;
  }

  .charity-header-button {
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s;
  }

  .charity-header-button:hover {
    background-color: #f3f4f6;
  }

  .charity-header-button:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    background-color: #f3f4f6;
  }

  .charity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    margin: 0;
  }

  .charity-header-content {
    flex: 1;
  }

  .charity-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .charity-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .charity-total {
    font-weight: 600;
    color: #111827;
  }

  .charity-contribution {
    font-weight: 500;
    color: #374151;
  }

  .expand-icon {
    flex-shrink: 0;
    color: #6b7280;
    transition: transform 0.2s ease;
    margin-left: 1rem;
  }

  .expand-icon.expanded {
    transform: rotate(180deg);
  }

  .donators-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.25rem;
    padding-top: 0.75rem;
    animation: slideDown 0.2s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 1000px;
    }
  }

  .donator-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .donator-info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .donator-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  .donator-hours {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .donator-amounts {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
  }

  .donator-total {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
  }

  .donator-contribution {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .included-note {
    font-size: 0.7em;
    color: #9ca3af;
    font-style: italic;
  }
</style>

